import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  type AnswerFileMetadata,
  extractAnswerFileMetadata,
  extractAnswerFileToken,
  fetchAnswerFileBlobUrl,
  guessIsImageFromToken,
  isImageMimeType,
} from "@/lib/answer-file";
import { isPhotoAnswer } from "@/lib/photo-answer";
import { formatDateTime } from "@/lib/utils";
import { FileText, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

function formatCaptureMetadata(meta: AnswerFileMetadata): string | null {
  const parts: string[] = [];
  if (
    typeof meta.latitude === "number" &&
    typeof meta.longitude === "number" &&
    Number.isFinite(meta.latitude) &&
    Number.isFinite(meta.longitude)
  ) {
    let coords = `${meta.latitude.toFixed(5)}, ${meta.longitude.toFixed(5)}`;
    if (
      typeof meta.accuracy_meters === "number" &&
      Number.isFinite(meta.accuracy_meters)
    ) {
      coords += ` (±${Math.round(meta.accuracy_meters)} m)`;
    }
    parts.push(coords);
  }
  if (
    typeof meta.distance_meters_from_property === "number" &&
    Number.isFinite(meta.distance_meters_from_property)
  ) {
    parts.push(
      `${Math.round(meta.distance_meters_from_property)} m from property`,
    );
  }
  if (typeof meta.captured_at === "string" && meta.captured_at.trim()) {
    const formatted = formatDateTime(meta.captured_at);
    const tz =
      typeof meta.timezone === "string" && meta.timezone.trim()
        ? ` (${meta.timezone.trim()})`
        : "";
    parts.push(`${formatted}${tz}`);
  } else if (typeof meta.timezone === "string" && meta.timezone.trim()) {
    parts.push(meta.timezone.trim());
  }
  if (
    typeof meta.server_skew_ms === "number" &&
    Number.isFinite(meta.server_skew_ms)
  ) {
    const skewSec = Math.round(Math.abs(meta.server_skew_ms) / 1000);
    parts.push(skewSec === 0 ? "time skew <1s" : `time skew ±${skewSec}s`);
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

function shouldShowAsImage(
  answer: unknown,
  answerType: string | undefined,
  contentType: string | null,
  token: string,
): boolean {
  if (answerType === "photo" || isPhotoAnswer(answer)) return true;
  if (isImageMimeType(contentType)) return true;
  if (!contentType && guessIsImageFromToken(token)) return true;
  return false;
}

interface AnswerFilePreviewProps {
  answer: unknown;
  answerType?: string;
  /** Prefer elevated dialog when nested in a Sheet. */
  elevatedDialog?: boolean;
  className?: string;
  /** Optional caption shown under the thumbnail (e.g. property address). */
  caption?: string | null;
  /** When true, omit the lat/lng · time metadata line (caller shows it elsewhere). */
  hideCaptureMetadata?: boolean;
}

export function AnswerFilePreview({
  answer,
  answerType,
  elevatedDialog = false,
  className,
  caption = null,
  hideCaptureMetadata = false,
}: AnswerFilePreviewProps) {
  const token = extractAnswerFileToken(answer);
  const metadata = extractAnswerFileMetadata(answer);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [contentType, setContentType] = useState<string | null>(
    metadata?.content_type ?? null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    if (!token) {
      setBlobUrl(null);
      setContentType(metadata?.content_type ?? null);
      setError(null);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    setLoading(true);
    setError(null);

    fetchAnswerFileBlobUrl(token)
      .then(({ blobUrl: url, contentType: mime }) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setBlobUrl(url);
        setContentType(mime || metadata?.content_type || null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load file");
        setBlobUrl(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [token, metadata?.content_type]);

  if (!token) {
    return <p className="text-sm text-muted-foreground">No file attached</p>;
  }

  const showAsImage = shouldShowAsImage(answer, answerType, contentType, token);
  const metaLine =
    !hideCaptureMetadata && metadata ? formatCaptureMetadata(metadata) : null;
  const captionLine = caption?.trim() ? caption.trim() : null;

  const openFull = () => {
    if (blobUrl) {
      setLightboxOpen(true);
    }
  };

  return (
    <div className={className}>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading preview…
        </div>
      ) : null}

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      {!loading && !error && showAsImage && blobUrl ? (
        <button
          type="button"
          className="group block overflow-hidden rounded border bg-muted/30 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={openFull}
          aria-label="View full-resolution image"
        >
          <img
            src={blobUrl}
            alt={captionLine ?? "Answer attachment"}
            className="max-h-36 max-w-[14rem] object-contain transition-opacity group-hover:opacity-90 sm:max-h-44"
          />
        </button>
      ) : null}

      {!loading && !error && !showAsImage && blobUrl ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-1"
          onClick={openFull}
        >
          <FileText className="h-4 w-4" />
          View file
        </Button>
      ) : null}

      {captionLine ? (
        <p
          className="mt-1 text-xs text-muted-foreground"
          data-testid="answer-file-caption"
        >
          {captionLine}
        </p>
      ) : null}

      {metaLine ? (
        <p className="mt-1 text-xs text-muted-foreground">{metaLine}</p>
      ) : null}

      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent
          elevated={elevatedDialog}
          className="max-h-[90vh] max-w-[min(96vw,56rem)] overflow-auto sm:max-w-[min(96vw,56rem)]"
        >
          <DialogHeader>
            <DialogTitle>Attachment</DialogTitle>
            <DialogDescription className="sr-only">
              Full-resolution attachment preview
            </DialogDescription>
          </DialogHeader>
          {blobUrl && showAsImage ? (
            <img
              src={blobUrl}
              alt="Full-resolution attachment"
              className="mx-auto max-h-[75vh] w-auto max-w-full object-contain"
            />
          ) : blobUrl ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                This file is not an image preview. Open it in a new tab.
              </p>
              <Button asChild variant="default" size="sm">
                <a href={blobUrl} target="_blank" rel="noreferrer">
                  Open file
                </a>
              </Button>
            </div>
          ) : null}
          {captionLine ? (
            <p className="text-xs text-muted-foreground">{captionLine}</p>
          ) : null}
          {metaLine ? (
            <p className="text-xs text-muted-foreground">{metaLine}</p>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
